import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { is } from 'date-fns/locale';
import {
  Clock,
  User,
  CheckCircle2,
  Circle,
  ArrowRight,
  FileText,
} from 'lucide-react';

/**
 * StageHistory Component
 *
 * Displays a vertical timeline of lead stage changes with:
 * - Stage transitions
 * - Timestamps (formatted in Icelandic)
 * - Agent names
 * - Optional notes
 */

export interface StageHistoryEntry {
  id: string;
  stage: string;
  timestamp: string;
  agent: string;
  note?: string;
}

interface StageHistoryProps {
  leadId?: string;
  history?: StageHistoryEntry[];
}

// Stage name translations
const STAGE_NAMES: Record<string, string> = {
  'nýr': 'Nýr',
  'haft_samband': 'Haft samband',
  'skoðun_bókuð': 'Skoðun bókuð',
  'tilboð_móttekið': 'Tilboð móttekið',
  'samningur_undirritaður': 'Samningur undirritaður',
  'lokað': 'Lokað',
  'glatað': 'Glatað',
};

// Stage icons
const getStageIcon = (stage: string) => {
  switch (stage) {
    case 'nýr':
      return <Circle className="h-4 w-4 text-blue-600" />;
    case 'haft_samband':
      return <User className="h-4 w-4 text-purple-600" />;
    case 'skoðun_bókuð':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'tilboð_móttekið':
      return <ArrowRight className="h-4 w-4 text-orange-600" />;
    case 'samningur_undirritaður':
      return <FileText className="h-4 w-4 text-indigo-600" />;
    case 'lokað':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'glatað':
      return <Circle className="h-4 w-4 text-red-600" />;
    default:
      return <Circle className="h-4 w-4 text-gray-400" />;
  }
};

export function StageHistory({ leadId, history: historyProp }: StageHistoryProps) {
  const [history, setHistory] = useState<StageHistoryEntry[]>(historyProp || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If history is provided as prop, use it
    if (historyProp) {
      setHistory(historyProp);
      return;
    }

    // If leadId is provided, fetch history from API
    if (leadId) {
      fetchHistory(leadId);
    }
  }, [leadId, historyProp]);

  const fetchHistory = async (_id: string) => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call when endpoint is implemented
      // const token = localStorage.getItem('token');
      // const response = await fetch(`/api/leads/${_id}/history`, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      // const data = await response.json();
      // setHistory(data.history);

      // Mock data for now
      setHistory([]);
    } catch (err) {
      console.error('Failed to fetch stage history:', err);
      setError('Gat ekki sótt sögu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPp', { locale: is });
    } catch {
      return dateString;
    }
  };

  const getStageName = (stage: string) => {
    return STAGE_NAMES[stage] || stage;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-2">{error}</p>
        {leadId && (
          <button
            onClick={() => fetchHistory(leadId)}
            className="text-sm text-prima-600 hover:text-prima-700 underline"
          >
            Reyna aftur
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">Engin saga enn</p>
        <p className="text-sm mt-1">
          Breytingar á stöðu munu birtast hér
        </p>
      </div>
    );
  }

  // Sort history by timestamp (newest first)
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <div className="relative">
      {/* Timeline vertical line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

      {/* Timeline events */}
      <div className="space-y-6">
        {sortedHistory.map((entry, index) => (
          <div key={entry.id} className="relative flex gap-4 pl-10">
            {/* Timeline dot with icon */}
            <div className="absolute left-0 top-1 bg-white border-2 border-gray-200 rounded-full p-1.5">
              {getStageIcon(entry.stage)}
            </div>

            {/* Event content */}
            <div className="flex-1 pb-6 border-b border-gray-100 last:border-0 last:pb-0">
              {/* Stage name */}
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">
                  {getStageName(entry.stage)}
                </h4>
                {index === 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    Núverandi
                  </span>
                )}
              </div>

              {/* Timestamp and agent */}
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <User className="h-3 w-3" />
                <span>{entry.agent}</span>
                <span>•</span>
                <Clock className="h-3 w-3" />
                <span>{formatDate(entry.timestamp)}</span>
              </div>

              {/* Optional note */}
              {entry.note && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                  <p className="whitespace-pre-wrap">{entry.note}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
